import React, { FC, useState } from 'react';
import { useContactCalendarOAuth } from '@wix/ai-scheduling-assistant-data-store';
import { Box, Button, Divider, Modal, ModalMobileLayout, Popover, Text } from '@wix/design-system';
import { useAuthTokenContext } from '../../../../context/AuthTokenContext';
import { useScreenWidth } from '../../../../hooks/useScreenWidth';
import { useUouLanguage } from '../../../../hooks/useUouLanguage';
import s from './uouChatHeader.scss';

type UoUChatHeaderPopoverMenuProps = {
  contactName: string;
};

const getContactInitials = (contactName: string) => {
  if (!contactName) {
    return '';
  }
  const [firstName, lastName = ''] = contactName.split(' ');

  return `${firstName.charAt(0).toLocaleUpperCase()}${lastName.charAt(0).toLocaleUpperCase()}`;
};

const ContactManagementContent: FC<{
  contactInitials: string;
  contactName: string;
}> = ({ contactInitials, contactName }) => {
  const language = useUouLanguage();
  const { authToken } = useAuthTokenContext();
  const { revokeToken } = useContactCalendarOAuth(authToken);

  return (
    <Box direction="vertical" gap={3} width="100%">
      <Box gap={2} verticalAlign="middle">
        <TriggerElement contactInitials={contactInitials} />
        <Box direction="vertical">
          <Text size="medium">{contactName}</Text>
          {/* <Text size="medium" skin="disabled">
            TODO: get the contact email
          </Text> */}
        </Box>
      </Box>
      <Divider direction="horizontal" />
      <Box>
        <Button className={s.disconnectButton} onClick={() => revokeToken()} skin="dark">
          {language.ASA_STANDALONE_HEADER_MENU_DISCONNECT_CTA()}
        </Button>
      </Box>
    </Box>
  );
};

const TriggerElement = ({ contactInitials, onClick }: { contactInitials: string; onClick?: () => void }) => (
  <Button className={s.userAvatar} skin="dark" priority="secondary" onClick={onClick}>
    {contactInitials}
  </Button>
);

export const ContactManagementModal: FC<UoUChatHeaderPopoverMenuProps> = ({ contactName }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const contactInitials = getContactInitials(contactName);

  return (
    <>
      <TriggerElement contactInitials={contactInitials} onClick={openModal} />
      <Modal isOpen={isOpen} shouldCloseOnOverlayClick>
        <ModalMobileLayout
          className={s.contactManagementModal}
          onOverlayClick={closeModal}
          onCloseButtonClick={closeModal}
          content={<ContactManagementContent contactInitials={contactInitials} contactName={contactName} />}
        />
      </Modal>
    </>
  );
};

export const ContactManagementMenu: FC<UoUChatHeaderPopoverMenuProps> = ({ contactName }) => {
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const togglePopover = () => setOpenPopover((prev) => !prev);
  const closePopover = () => setOpenPopover(false);
  const contactInitials = getContactInitials(contactName);
  const { isMobile } = useScreenWidth();

  return (
    <Popover animate showArrow placement="left-start" shown={openPopover} onClickOutside={closePopover}>
      <Popover.Element>
        <TriggerElement contactInitials={contactInitials} onClick={togglePopover} />
      </Popover.Element>
      <Popover.Content>
        <Box padding="12px 24px" width={isMobile ? 200 : 290}>
          <ContactManagementContent contactInitials={contactInitials} contactName={contactName} />
        </Box>
      </Popover.Content>
    </Popover>
  );
};
